import React from 'react';
import { TaskWithState, TaskState } from '../../types';
import { createWedgePath60min, createRimWedgePath60min } from '../../utils/clockMath';
import styles from './TaskWedge.module.scss';

interface TaskWedgeProps {
  task: TaskWithState;
  cx: number;
  cy: number;
  radius: number;
  innerRadius?: number; // Optional inner radius for rim style
  /** Override visible start minute (for truncation) */
  visibleStartMin?: number;
  /** Override visible end minute (for truncation) */
  visibleEndMin?: number;
  /** Gradient start opacity (0.0-1.0) */
  alphaStart?: number;
  /** Gradient end opacity (0.0-1.0) */
  alphaEnd?: number;
  /** Segment index (for unique gradient IDs when task is split) */
  segmentIndex?: number;
  /** Does this segment have a next segment for same task? (affects stroke) */
  hasNextSegment?: boolean;
  /** Is this task (or any of its segments) currently hovered? */
  isHovered?: boolean;
  /** Handler when mouse enters this segment */
  onHover?: (task: TaskWithState) => void;
  /** Handler when mouse leaves this segment */
  onLeave?: (task: TaskWithState) => void;
  /** Handler when clicked */
  onClick?: (task: TaskWithState) => void;
  /** Whether this task just became active (for MinuLine crossing pulse) */
  justBecameActive?: boolean;
}

export function TaskWedge({ 
  task, 
  cx, 
  cy, 
  radius, 
  visibleStartMin,
  visibleEndMin,
  alphaStart = 1.0,
  alphaEnd = 1.0,
  segmentIndex = 0,
  hasNextSegment = false,
  innerRadius = 0,
  isHovered = false,
  onHover,
  onLeave,
  onClick,
  justBecameActive = false
}: TaskWedgeProps) {
  // Use visible minutes if provided (for truncation), otherwise use actual task times
  const startMinutes = visibleStartMin ?? task.startTime.getMinutes();
  const endMinutes = visibleEndMin ?? task.endTime.getMinutes();

  // Determine visual state
  const isActive = task.state === TaskState.ACTIVE;
  const isCompleted = task.isCompleted;
  const isPastDue = task.state === TaskState.PAST_DUE_WINDOW;
  const isFading = task.state === TaskState.FADING_OUT;

  const shouldPulse = task.shouldPulse && !isCompleted;

  // Build class names
  const wedgeClasses = [
    styles.wedge,
    isActive && styles.active,
    isCompleted && styles.completed,
    isPastDue && styles.pastDue,
    isFading && styles.fading,
    shouldPulse && styles.pulse,
    isHovered && styles.hovered,
    justBecameActive && styles.minulinePulse
  ].filter(Boolean).join(' ');
  
  // Always use micro-segmentation for consistent rendering (prevents seams)
  let duration = endMinutes - startMinutes;
  if (duration < 0) duration += 60;
  
  // Stabilize duration to avoid floating point jitter causing segment count fluctuation
  // Round to 3 decimal places (approx 60ms precision)
  duration = Math.round(duration * 1000) / 1000;
  
  // Split into quarter-minute segments (15 seconds each) for ultra-smooth gradient
  const SEGMENTS_PER_MINUTE = 4;
  const totalSegments = Math.max(1, Math.round(duration * SEGMENTS_PER_MINUTE));
  const microSegments = [];
  
  // Debug first segment if it has gradient
  // if (segmentIndex === 0 && Math.abs(alphaStart - alphaEnd) > 0.01) {
  //   console.log...
  // }
  
  for (let i = 0; i < totalSegments; i++) {
    const segmentFraction = (i * duration) / totalSegments;
    const nextSegmentFraction = ((i + 1) * duration) / totalSegments;
    
    const segStartMin = (startMinutes + segmentFraction) % 60;
    const segEndMin = (startMinutes + nextSegmentFraction) % 60;
    
    // Calculate alpha for this micro-segment (use midpoint)
    const progress = (i + 0.5) / totalSegments;
    const alpha = alphaStart + (alphaEnd - alphaStart) * progress;
    
    microSegments.push(
      <path
        key={`micro-${i}`}
        d={innerRadius > 0 
          ? createRimWedgePath60min(cx, cy, radius, innerRadius, segStartMin, segEndMin)
          : createWedgePath60min(cx, cy, radius, segStartMin, segEndMin)
        }
        fill={task.color}
        fillOpacity={alpha}
        stroke="none"
        strokeWidth="0"
        style={{ opacity: 1.0 }}
        className={styles.wedgePath}
      />
    );
  }
  
  return (
    <g 
      className={wedgeClasses}
      onMouseEnter={() => onHover?.(task)}
      onMouseLeave={() => onLeave?.(task)}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(task);
      }}
    >
      {microSegments}
    </g>
  );
}
